/**
 * Import/eksport harmonogramów — PDF, XLSX, CSV.
 * Wszystkie operacje wykonujemy po stronie przeglądarki, żeby nie obciążać backendu.
 *
 * Kolumny zgodne z UI (ID, SPP, Opis, Dni, Rozpoczęcie, Zakończenie, Poprzednik,
 * Wartość, Uwagi, Zaaw. [%], Data stanu).
 */

import * as XLSX from 'xlsx';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import type { ScheduleTask, ScheduleTaskInput } from '../../services/schedulesService';

export const EXPORT_COLUMNS = [
  'ID',
  'SPP',
  'Opis',
  'Dni',
  'Rozpoczęcie',
  'Zakończenie',
  'Poprzednik',
  'Wartość',
  'Uwagi',
  'Zaaw. [%]',
  'Data stanu',
] as const;

/** Konwertuje listę zadań do tablicy wierszy gotowej do eksportu. */
function tasksToRows(tasks: ScheduleTask[]): (string | number)[][] {
  return tasks.map((t) => [
    t.ordinal,
    t.ssp || '',
    t.opis || '',
    t.days,
    t.start_date || '',
    t.end_date || '',
    t.predecessor || '',
    Number(t.value) || 0,
    t.notes || '',
    Number(t.progress_pct) || 0,
    t.progress_date || '',
  ]);
}

/** Bezpieczny snake-case dla nazw plików (usuwa polskie znaki). */
function safeFileName(name: string): string {
  return String(name || 'harmonogram')
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-zA-Z0-9_-]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 80) || 'harmonogram';
}

/* ================== EXPORT ================== */

export function exportToCsv(scheduleName: string, tasks: ScheduleTask[]): void {
  const header = EXPORT_COLUMNS.join(';');
  const body = tasksToRows(tasks)
    .map((row) =>
      row
        .map((cell) => {
          const s = String(cell ?? '');
          if (s.includes(';') || s.includes('"') || s.includes('\n')) return `"${s.replace(/"/g, '""')}"`;
          return s;
        })
        .join(';')
    )
    .join('\r\n');
  const content = `\uFEFF${header}\r\n${body}`;
  const blob = new Blob([content], { type: 'text/csv;charset=utf-8' });
  triggerDownload(blob, `${safeFileName(scheduleName)}.csv`);
}

export function exportToXlsx(scheduleName: string, tasks: ScheduleTask[]): void {
  const aoa: (string | number)[][] = [EXPORT_COLUMNS as unknown as string[], ...tasksToRows(tasks)];
  const ws = XLSX.utils.aoa_to_sheet(aoa);
  ws['!cols'] = [
    { wch: 6 }, { wch: 10 }, { wch: 50 }, { wch: 6 }, { wch: 12 },
    { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 28 }, { wch: 10 }, { wch: 12 },
  ];
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Harmonogram');
  XLSX.writeFile(wb, `${safeFileName(scheduleName)}.xlsx`);
}

/** Prosty eksport PDF (tabela) — landscape, A4, Roboto-like. */
export function exportToPdf(scheduleName: string, tasks: ScheduleTask[]): void {
  const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });

  doc.setFontSize(14);
  doc.text(scheduleName || 'Harmonogram', 40, 36);
  doc.setFontSize(9);
  doc.text(`Wygenerowano: ${new Date().toLocaleString('pl-PL')}`, 40, 52);

  autoTable(doc, {
    startY: 64,
    head: [EXPORT_COLUMNS as unknown as string[]],
    body: tasksToRows(tasks).map((r) => r.map((c) => (typeof c === 'number' ? String(c) : c))),
    styles: { fontSize: 8, cellPadding: 3, overflow: 'linebreak' },
    headStyles: { fillColor: [2, 2, 66], textColor: 255, fontStyle: 'bold' },
    columnStyles: {
      0: { cellWidth: 28 },
      1: { cellWidth: 50 },
      2: { cellWidth: 220 },
      3: { cellWidth: 30 },
      4: { cellWidth: 58 },
      5: { cellWidth: 58 },
      6: { cellWidth: 55 },
      7: { cellWidth: 60, halign: 'right' },
      8: { cellWidth: 110 },
      9: { cellWidth: 42, halign: 'right' },
      10: { cellWidth: 60 },
    },
  });

  doc.save(`${safeFileName(scheduleName)}.pdf`);
}

function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 2000);
}

/* ================== IMPORT ================== */

const HEADER_ALIASES: Record<string, string[]> = {
  ssp: ['spp', 'ssp', 'wbs'],
  opis: ['opis', 'zadanie', 'task', 'name'],
  days: ['dni', 'days'],
  start_date: ['rozpoczecie', 'rozpoczęcie', 'start', 'start date', 'start_date'],
  end_date: ['zakonczenie', 'zakończenie', 'koniec', 'end', 'end date', 'end_date'],
  predecessor: ['poprzednik', 'predecessor'],
  value: ['wartosc', 'wartość', 'value'],
  notes: ['uwagi', 'notes'],
  progress_pct: ['zaaw. [%]', 'zaawansowanie', 'zaaw.', 'progress', 'progress_pct'],
  progress_date: ['data stanu', 'data_stanu', 'progress_date'],
};

function mapHeaderRow(header: string[]): Record<string, number> {
  const m: Record<string, number> = {};
  header.forEach((h, idx) => {
    const norm = String(h || '').trim().toLowerCase();
    for (const [key, aliases] of Object.entries(HEADER_ALIASES)) {
      if (aliases.some((a) => a === norm)) {
        if (m[key] == null) m[key] = idx;
      }
    }
  });
  return m;
}

function toIsoDate(raw: unknown): string {
  if (!raw) return '';
  if (raw instanceof Date) return raw.toISOString().slice(0, 10);
  const s = String(raw).trim();
  if (!s) return '';
  /* Akceptujemy YYYY-MM-DD, YYYY-MM-DD, DD.MM.YYYY, DD/MM/YYYY, excel date number. */
  const iso = /^\d{4}-\d{2}-\d{2}/.exec(s);
  if (iso) return s.slice(0, 10);
  const m1 = /^(\d{2})[./-](\d{2})[./-](\d{4})$/.exec(s);
  if (m1) return `${m1[3]}-${m1[2]}-${m1[1]}`;
  const num = Number(s);
  if (Number.isFinite(num) && num > 20000 && num < 80000) {
    /* Excel serial date (dni od 1899-12-30). */
    const base = new Date(Date.UTC(1899, 11, 30));
    base.setUTCDate(base.getUTCDate() + num);
    return base.toISOString().slice(0, 10);
  }
  const d = new Date(s);
  if (!Number.isNaN(d.getTime())) return d.toISOString().slice(0, 10);
  return '';
}

function parsePlNumber(raw: unknown): number {
  if (raw == null || raw === '') return 0;
  const n = Number(String(raw).replace(/\s/g, '').replace(',', '.'));
  return Number.isFinite(n) ? n : 0;
}

function rowsToTaskInputs(header: string[], rows: unknown[][]): ScheduleTaskInput[] {
  const map = mapHeaderRow(header);
  const today = new Date().toISOString().slice(0, 10);
  const out: ScheduleTaskInput[] = [];
  for (const row of rows) {
    if (!row || row.every((v) => v == null || String(v).trim() === '')) continue;
    const opis = String(row[map.opis] ?? '').trim();
    if (!opis) continue;
    const days = Math.max(1, Math.floor(parsePlNumber(row[map.days]) || 1));
    const start_date = toIsoDate(row[map.start_date]) || today;
    const end_date_raw = toIsoDate(row[map.end_date]);
    out.push({
      ssp: String(row[map.ssp] ?? '').trim(),
      opis,
      days,
      start_date,
      end_date: end_date_raw || undefined,
      predecessor: row[map.predecessor] ? String(row[map.predecessor]).trim() : undefined,
      value: parsePlNumber(row[map.value]),
      notes: row[map.notes] ? String(row[map.notes]) : undefined,
      progress_pct: Math.max(0, Math.min(100, parsePlNumber(row[map.progress_pct]))),
      progress_date: row[map.progress_date] ? toIsoDate(row[map.progress_date]) || undefined : undefined,
    });
  }
  return out;
}

export async function importFromFile(file: File): Promise<ScheduleTaskInput[]> {
  const lower = file.name.toLowerCase();
  if (lower.endsWith('.csv')) return importFromCsv(file);
  if (lower.endsWith('.xlsx') || lower.endsWith('.xls')) return importFromXlsx(file);
  throw new Error('Nieobsługiwany format. Użyj CSV lub XLSX.');
}

async function importFromCsv(file: File): Promise<ScheduleTaskInput[]> {
  const text = await file.text();
  const firstLine = text.split(/\r?\n/)[0] || '';
  const sep = (firstLine.match(/;/g) || []).length > (firstLine.match(/,/g) || []).length ? ';' : ',';
  const rows = parseCsvSimple(text, sep);
  if (!rows.length) return [];
  const [header, ...body] = rows;
  return rowsToTaskInputs(header, body);
}

async function importFromXlsx(file: File): Promise<ScheduleTaskInput[]> {
  const buf = await file.arrayBuffer();
  const wb = XLSX.read(buf, { type: 'array', cellDates: true });
  const ws = wb.Sheets[wb.SheetNames[0]];
  const aoa: unknown[][] = XLSX.utils.sheet_to_json(ws, { header: 1, raw: true, defval: '' });
  if (!aoa.length) return [];
  const [header, ...body] = aoa;
  return rowsToTaskInputs((header as unknown[]).map((x) => String(x ?? '')), body);
}

/** Minimalny parser CSV – ";" albo "," , cudzysłowy z eskejpem. */
function parseCsvSimple(text: string, sep: string): string[][] {
  const rows: string[][] = [];
  let field = '';
  let row: string[] = [];
  let inQuotes = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (ch === '"') {
      if (inQuotes && text[i + 1] === '"') { field += '"'; i++; continue; }
      inQuotes = !inQuotes;
      continue;
    }
    if (!inQuotes && ch === sep) { row.push(field); field = ''; continue; }
    if (!inQuotes && (ch === '\n' || ch === '\r')) {
      if (ch === '\r' && text[i + 1] === '\n') i++;
      row.push(field);
      rows.push(row);
      row = [];
      field = '';
      continue;
    }
    field += ch;
  }
  if (field.length > 0 || row.length > 0) {
    row.push(field);
    rows.push(row);
  }
  return rows.filter((r) => r.some((c) => String(c).trim() !== ''));
}
